import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import api from "../lib/api";
import {
  clearInviteToken,
  inviteLinkExpired,
  inviteLinkFailed,
  landedAsAuthInvite,
  readInviteToken,
} from "../lib/inviteToken";
import { platformErrorMessage } from "../lib/platformValidation";
import { reportError } from "../lib/errorReporter";
import { fmtDateTime } from "../lib/format";
import InviteShell from "../components/invite/InviteShell";
import InviteSetPassword from "../components/invite/InviteSetPassword";

// How long to wait for the Supabase SDK to turn an invitation link into a
// session before the screen gives up on it.
const SESSION_TIMEOUT_MS = 10000;

/** The token is sent in the BODY, never in a URL. Absent for the new-user flow. */
function tokenBody() {
  const token = readInviteToken();
  return token ? { token } : {};
}

/**
 * Owner invitation acceptance — the only path that activates an organization
 * provisioned by an OfferTrace platform administrator.
 *
 * PUBLIC route, deliberately outside the organization guards: the person
 * accepting belongs to no organization yet, which is exactly what acceptance
 * fixes. Nothing here is authorized by the URL:
 *
 *   * the one-time token arrives in the FRAGMENT, is captured and stripped
 *     before this component ever renders (see lib/inviteToken.js) and is sent to
 *     the backend in a request body only;
 *   * a genuine Supabase session is mandatory in both flows, and the backend
 *     re-checks that the session e-mail matches the invited address;
 *   * the organization, the role and the seat are decided by the database.
 */
export default function AcceptOwnerInvite() {
  const { t } = useTranslation();
  const { loading, session, refreshOrgs } = useAuth();
  const [phase, setPhase] = useState(() => (inviteLinkFailed() ? "link_error" : "loading"));
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [waited, setWaited] = useState(false);
  const started = useRef(false);
  const inFlight = useRef(false);
  const authInvite = landedAsAuthInvite();

  // Belt and braces on top of the fragment: no Referer may ever leave this
  // route, whatever a future link on the page might be.
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "referrer";
    meta.content = "no-referrer";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, []);

  useEffect(() => {
    if (!authInvite || session || waited) return undefined;
    const timer = setTimeout(() => setWaited(true), SESSION_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [authInvite, session, waited]);

  const load = useCallback(async () => {
    try {
      const r = await api.post("/owner-invitations/preview", tokenBody(), {
        skipOrgHeader: true,
      });
      setPreview(r.data);
      const state = r.data?.state;
      if (state === "valid") {
        setPhase(authInvite && r.data?.flow === "new_user" ? "set_password" : "ready");
      } else if (state === "requires_authentication") {
        setPhase("requires_authentication");
      } else if (state === "already_accepted") {
        setPhase("already");
      } else {
        setPhase("refused");
      }
    } catch (err) {
      reportError("invite.preview");
      setError(platformErrorMessage(err, t("invite.error_generic")));
      setPhase("refused");
    }
  }, [authInvite, t]);

  useEffect(() => {
    if (started.current || loading || phase !== "loading") return;
    // Give the SDK a chance to establish the session an invitation link carries.
    if (authInvite && !session && !waited) return;
    started.current = true;
    load();
  }, [authInvite, load, loading, phase, session, waited]);

  const accept = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setError("");
    setPhase("working");
    try {
      const r = await api.post("/owner-invitations/accept", tokenBody(), {
        skipOrgHeader: true,
      });
      clearInviteToken();
      setPreview((current) => ({
        ...(current || {}),
        organization_name: r.data?.organization_name || current?.organization_name,
      }));
      try {
        await refreshOrgs();
      } catch (err) {
        reportError("invite.refresh_orgs");
      }
      setPhase(r.data?.already_accepted ? "already" : "success");
    } catch (err) {
      reportError("invite.accept");
      const code = err?.response?.data?.detail?.code;
      setError(platformErrorMessage(err, t("invite.error_generic")));
      setPhase(code === "not_authenticated" ? "requires_authentication" : "refused");
    } finally {
      inFlight.current = false;
    }
  }, [refreshOrgs, t]);

  const setPasswordThenAccept = useCallback(
    async (password) => {
      if (inFlight.current) return;
      inFlight.current = true;
      setError("");
      const { error: updateError } = await supabase.auth.updateUser({ password });
      inFlight.current = false;
      if (updateError) {
        reportError("invite.set_password");
        if (updateError.code === "weak_password") setError(t("password.error_policy"));
        else if (updateError.status === 401 || updateError.status === 403) {
          setPhase("link_error");
        } else setError(t("password.error_generic"));
        return;
      }
      await accept();
    },
    [accept, t],
  );

  const organizationName = preview?.organization_name || "";

  const body = () => {
    if (phase === "loading") {
      return (
        <div className="qw-empty" data-testid="accept-invite-checking">
          {t("invite.checking")}
        </div>
      );
    }

    if (phase === "link_error") {
      return (
        <div data-testid="accept-invite-link-error">
          <h1>{t("invite.link_error_title")}</h1>
          <p className="lead">
            {inviteLinkExpired() ? t("invite.link_expired_body") : t("invite.link_error_body")}
          </p>
          <Link className="qw-btn primary" to="/login" data-testid="accept-invite-go-to-login">
            {t("invite.go_to_login")}
          </Link>
        </div>
      );
    }

    if (phase === "requires_authentication") {
      return (
        <div data-testid="accept-invite-requires-auth">
          <h1>{t("invite.requires_auth_title")}</h1>
          <p className="lead">{t("invite.requires_auth_body")}</p>
          {organizationName && (
            <div className="qw-help" data-testid="accept-invite-organization">
              {t("invite.organization")}: <strong>{organizationName}</strong>
            </div>
          )}
          <Link
            className="qw-btn primary"
            to="/login"
            style={{ marginTop: 12 }}
            data-testid="accept-invite-go-to-login"
          >
            {t("invite.go_to_login")}
          </Link>
        </div>
      );
    }

    if (phase === "set_password") {
      return (
        <InviteSetPassword busy={false} error={error} onSubmit={setPasswordThenAccept} />
      );
    }

    if (phase === "success") {
      return (
        <div data-testid="accept-invite-success">
          <h1>{t("invite.success_title")}</h1>
          <p className="lead">{t("invite.success_lead", { name: organizationName })}</p>
          <Link className="qw-btn primary" to="/dashboard" data-testid="accept-invite-go-to-app">
            {t("invite.go_to_app")}
          </Link>
        </div>
      );
    }

    if (phase === "already") {
      return (
        <div data-testid="accept-invite-already">
          <h1>{t("invite.already_title")}</h1>
          <p className="lead">{preview?.message || t("invite.already_body")}</p>
          <Link className="qw-btn primary" to="/dashboard" data-testid="accept-invite-go-to-app">
            {t("invite.go_to_app")}
          </Link>
        </div>
      );
    }

    if (phase === "refused") {
      return (
        <div data-testid="accept-invite-refused">
          <h1>{t("invite.refused_title")}</h1>
          <p className="lead" data-testid="accept-invite-refused-message">
            {error || preview?.message || t("invite.error_generic")}
          </p>
          <Link className="qw-btn primary" to="/login" data-testid="accept-invite-go-to-login">
            {t("invite.go_to_login")}
          </Link>
        </div>
      );
    }

    return (
      <div data-testid="accept-invite-ready">
        <h1>{t("invite.valid_title")}</h1>
        <p className="lead">{t("invite.valid_lead", { name: organizationName })}</p>
        {preview?.expires_at && (
          <div className="qw-help" data-testid="accept-invite-expires">
            {t("invite.expires_at")}: {fmtDateTime(preview.expires_at)}
          </div>
        )}
        {error && (
          <div className="qw-error" data-testid="accept-invite-error">
            {error}
          </div>
        )}
        <button
          type="button"
          className="qw-btn primary"
          style={{ width: "100%", justifyContent: "center", marginTop: 12 }}
          onClick={accept}
          disabled={phase === "working"}
          data-testid="accept-invite-submit"
        >
          {phase === "working" ? t("invite.accepting") : t("invite.accept")}
        </button>
      </div>
    );
  };

  return <InviteShell>{body()}</InviteShell>;
}
